import { IntegrationType } from '@principle-theorem/integrations';
import {
  INamedDocument,
  Region,
  type ISODateType,
  type WithRef,
} from '@principle-theorem/shared';
import { BillingMeterPriceLookupId } from '../organisation/metered-billing';
import { IPractice } from '../practice/practice';
import { IPodiumIntegrationData } from './podium-integration';
import { type SMSConversation, type SMSMessage } from './sms-message';
import { ITNZIntegrationData } from './tnz-integration';
import { ITwilioIntegrationData } from './twilio-integration';
import { IViaApiIntegrationData } from './via-api-integration';

export const SMS_INTEGRATION_OPTIONS = [
  IntegrationType.Podium,
  IntegrationType.Twilio,
  IntegrationType.TNZ,
  IntegrationType.ViaApi,
];

export type SmsIntegrationOption =
  | IntegrationType.Podium
  | IntegrationType.Twilio
  | IntegrationType.TNZ
  | IntegrationType.ViaApi;

export const SMS_INTEGRATION_COUNTRY_MAP: Record<
  Region,
  SmsIntegrationOption[]
> = {
  [Region.Australia]: [IntegrationType.Twilio, IntegrationType.Podium],
  [Region.NewZealand]: [IntegrationType.TNZ],
};

export const VIA_API_SMS_INTEGRATION_OPTION: SmsIntegrationOption =
  IntegrationType.ViaApi;

export enum ISORegionCode {
  Australia = 'AU',
  NewZealand = 'NZ',
}

export const SUPPORTED_REGION_CODE_MAP: Record<Region, ISORegionCode> = {
  [Region.Australia]: ISORegionCode.Australia,
  [Region.NewZealand]: ISORegionCode.NewZealand,
};

export const SMS_INTEGRATION_REGION_CODE_MAP: Record<
  SmsIntegrationOption,
  ISORegionCode[]
> = {
  [IntegrationType.Podium]: [ISORegionCode.Australia],
  [IntegrationType.Twilio]: [ISORegionCode.Australia],
  [IntegrationType.TNZ]: [ISORegionCode.Australia, ISORegionCode.NewZealand],
  [IntegrationType.ViaApi]: [
    ISORegionCode.Australia,
    ISORegionCode.NewZealand,
  ],
};

export interface ISMSProvider {
  sendSMS(data: WithRef<SMSMessage>): Promise<void>;
  createConversation(conversation: WithRef<SMSConversation>): Promise<void>;
  closeConversation(conversation: WithRef<SMSConversation>): Promise<void>;
  reopenConversation(conversation: WithRef<SMSConversation>): Promise<void>;
}

export interface ISMSUsageRequest {
  from: ISODateType;
  to: ISODateType;
  orgUid: string;
}

export interface ITwilioMessageBillingSummary {
  dateSent: Date;
  numSegments: string;
  price: number;
}

export interface ITwilioBillingSummary {
  practice: INamedDocument<IPractice>;
  inboundTotal: number;
  outboundTotal: number;
  total: number;
  inbound: ITwilioMessageBillingSummary[];
  inboundSegments: number;
  outbound: ITwilioMessageBillingSummary[];
  outboundSegments: number;
}

export interface ISMSUsageResponse {
  practices: ITwilioBillingSummary[];
}

export interface IPodiumSMSLocationsRequest {
  orgUid: string;
}

export interface IPodiumSMSLocation {
  uid: string;
  label: string;
}

export interface IPodiumSMSLocationsResponse {
  locations: IPodiumSMSLocation[];
}

export interface IBaseSMSIntegrationData {
  /**
   * @deprecated
   * This will be replaced with billing meter IDs
   */
  inboundCost?: number;

  /**
   * @deprecated
   * This will be replaced with billing meter IDs
   */
  outboundCost?: number;

  inboundPriceId?: BillingMeterPriceLookupId;
  outboundPriceId?: BillingMeterPriceLookupId;
  marketingPriceId?: BillingMeterPriceLookupId;
}

export type ISMSIntegrationData =
  | ITwilioIntegrationData
  | IPodiumIntegrationData
  | ITNZIntegrationData
  | IViaApiIntegrationData;
